<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  CARD_SEO_DATA,
  MAJOR_ARCANA_SLUGS,
  MINOR_ARCANA_SLUGS,
  type CardSeoData,
} from '@app/data/cardSeoData';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

const YESNO_TITLE = 'Таро да или нет онлайн — значение всех карт | Таро Мяу';
const YESNO_DESCRIPTION =
  'Таро да или нет онлайн: какие карты значат «да», «нет» или «зависит» — все 78 карт с кратким объяснением. И бесплатный расклад «да/нет» в один клик.';

// All 78 cards in canonical deck order.
const deckOrder = [...MAJOR_ARCANA_SLUGS, ...MINOR_ARCANA_SLUGS];
const allCards: CardSeoData[] = deckOrder.map((s) => CARD_SEO_DATA[s]).filter(Boolean);

const yesCards = computed(() => allCards.filter((c) => c.correspondences.yesNo === 'yes'));
const noCards = computed(() => allCards.filter((c) => c.correspondences.yesNo === 'no'));
const maybeCards = computed(() => allCards.filter((c) => c.correspondences.yesNo === 'maybe'));

const columns = computed(() => [
  {
    key: 'yes',
    label: 'Да',
    note: 'Карты, которые склоняют к положительному ответу.',
    cards: yesCards.value,
    badge: 'bg-success text-success-foreground',
    tilt: '-2deg',
  },
  {
    key: 'no',
    label: 'Нет',
    note: 'Карты, которые скорее говорят «нет» или «не сейчас».',
    cards: noCards.value,
    badge: 'bg-destructive text-destructive-foreground',
    tilt: '1.5deg',
  },
  {
    key: 'maybe',
    label: 'Зависит',
    note: 'Карты, ответ которых зависит от контекста и твоих действий.',
    cards: maybeCards.value,
    badge: 'bg-warning text-warning-foreground',
    tilt: '-1deg',
  },
]);

useHead({
  title: YESNO_TITLE,
  meta: [
    { name: 'description', content: YESNO_DESCRIPTION },
    {
      name: 'keywords',
      content:
        'таро да или нет, да нет таро значение, какие карты таро да, какие карты таро нет, гадание да нет, расклад да или нет',
    },
    { property: 'og:title', content: YESNO_TITLE },
    { property: 'og:description', content: YESNO_DESCRIPTION },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: YESNO_TITLE },
    { name: 'twitter:description', content: YESNO_DESCRIPTION },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/karty/da-ili-net' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/karty/da-ili-net' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/karty/da-ili-net' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Карты', item: 'https://tarotmeow.ru/karty' },
          { '@type': 'ListItem', position: 3, name: 'Да или Нет', item: 'https://tarotmeow.ru/karty/da-ili-net' },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Таро да или нет — значение карт',
        numberOfItems: allCards.length,
        itemListElement: allCards.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: c.name,
          url: `https://tarotmeow.ru/karty/${c.slug}`,
        })),
      }),
    },
  ],
});
</script>

<template>
  <div class="py-16 md:py-24">
    <div class="site-container">
      <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Карты', to: '/karty' }, { label: 'Да или Нет' }]" />

      <!-- Header -->
      <header class="mb-10 text-center md:mb-14">
        <div class="type-overline mb-3.5 text-primary-ink">Быстрый ответ</div>
        <h1 class="type-display">Таро: да или нет</h1>
        <p class="type-body mx-auto mt-3.5 max-w-2xl text-muted-foreground md:text-[17px]">
          Когда нужен короткий ответ, у каждой карты есть склонность к «да», «нет» или «зависит». Ниже
          — все 78 карт, разнесённые по ответу, с одной строкой объяснения. Это ориентир, а финальный
          смысл всегда уточняет твой вопрос.
        </p>
      </header>

      <!-- Как читать «да/нет» -->
      <ScrollReveal class="paper-sheet mx-auto mb-14 max-w-[820px] p-6 md:mb-16 md:p-7">
        <h2 class="type-subheading text-foreground">Как читать «да / нет» по картам</h2>
        <p class="type-body mt-2.5 text-muted-foreground">
          Светлые, движущие вперёд карты обычно отвечают «да». Карты потери, паузы и кризиса
          склоняются к «нет». А карты выбора, тумана и зависимости от контекста дают «зависит» — их
          ответ решают твои действия. Перевёрнутое положение ослабляет «да» и усиливает «нет», но не
          переворачивает смысл механически.
        </p>
      </ScrollReveal>

      <!-- Три колонки: Да / Нет / Зависит — a sheet each, the answer stamped on a coloured tag -->
      <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <section
          v-for="col in columns"
          :key="col.key"
          class="paper-sheet flex flex-col p-5 md:p-6"
        >
          <div class="mb-1.5 flex items-center gap-3">
            <span
              class="seo-tag type-overline px-2.5 py-0.5"
              :class="col.badge"
              :style="{ '--tilt': col.tilt }"
            >
              {{ col.cards.length }}
            </span>
            <h2 class="type-heading">{{ col.label }}</h2>
          </div>
          <p class="type-caption mb-3">{{ col.note }}</p>

          <ul class="seo-lines">
            <li
              v-for="card in col.cards"
              :key="card.slug"
            >
              <RouterLink
                :to="`/karty/${card.slug}`"
                class="dn-row group block py-3"
              >
                <span class="type-app-heading text-foreground transition-colors group-hover:text-primary-ink">{{ card.name }}</span>
                <span class="type-caption mt-1 block">{{ card.correspondences.yesNoReason }}</span>
              </RouterLink>
            </li>
          </ul>
        </section>
      </div>

      <!-- Методическая заметка -->
      <ScrollReveal class="paper-sheet mx-auto mt-14 max-w-[820px] p-6 md:mt-16 md:p-7">
        <h2 class="type-subheading text-foreground">Важно про метод «да / нет»</h2>
        <p class="type-body mt-2.5 text-muted-foreground">
          «Да / нет» — это упрощение, удобный быстрый ориентир, а не приговор. Одна карта не знает
          всех обстоятельств твоей жизни, поэтому относись к ответу как к подсказке, которую стоит
          сверить со здравым смыслом. Хочешь больше нюанса — задай вопрос так, чтобы ответ помогал
          действовать, и собери полноценный расклад.
        </p>
      </ScrollReveal>

      <!-- CTA -->
      <ScrollReveal class="mt-14 md:mt-16">
        <SeoFinalCta>
          <h2 class="type-title">Спроси карты о своём</h2>
          <p class="type-body mx-auto mt-3.5 max-w-xl text-muted-foreground md:text-[17px]">
            Не хочешь искать карту в таблице — открой расклад «Да или Нет» и получи короткий ответ с
            персональным разбором. Бесплатно и без регистрации.
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink to="/rasklady/da-ili-net">Расклад «Да или Нет»</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </div>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
.dn-row {
  border-radius: var(--paper-radius-xs);
}
.dn-row:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 2px;
}
</style>
